import apiClient from './ApiClient';
import SessionManager from '../utils/SessionManager';
import ErrorHandler from '../utils/ErrorHandler';
import { API_CONFIG } from '../config/api';

/**
 * Authentication Service - Manages user authentication and session state
 * Handles login, logout, token management, and session validation
 */
class AuthService {
  constructor() {
    this.isLoggingIn = false;
    this.currentUser = null;
  }

  /**
   * Login with username and password
   * API expects: {"username": "bob","password": "password#2@#"}
   * API returns: {"access_token": "SiqXyi5o","token_type": "bearer"}
   */
  async login(credentials) {
    if (this.isLoggingIn) {
      throw new Error('Login already in progress');
    }

    if (!credentials || !credentials.username || !credentials.password) {
      throw new Error('Username and password are required');
    }

    this.isLoggingIn = true;

    try {
      // Make login request to API
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.LOGIN, {
        username: credentials.username.trim(),
        password: credentials.password
      }, { skipAuth: true }); // Skip auth for login request

      // Validate response format
      if (!response.access_token) {
        throw new Error('Invalid response format: missing access_token');
      }

      // Store token and user info
      const success = SessionManager.setToken(response.access_token);
      if (!success) {
        throw new Error('Failed to store authentication token');
      }

      // Create user info from response (if available) or from credentials
      const userInfo = {
        username: credentials.username,
        token_type: response.token_type || 'bearer',
        loginTime: new Date().toISOString()
      };

      SessionManager.setUser(userInfo);
      this.currentUser = userInfo;

      return {
        success: true,
        user: userInfo,
        token: response.access_token,
        message: 'Login successful'
      };

    } catch (error) {
      // Handle specific login errors
      if (error.status === 401) {
        throw new Error('Invalid username or password');
      } else if (error.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.name === 'NetworkError') {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      } else if (error.name === 'TimeoutError') {
        throw new Error('Login request timed out. Please try again.');
      }
      
      // Re-throw with original message or generic error
      throw new Error(error.message || 'Login failed. Please try again.');
    } finally {
      this.isLoggingIn = false;
    }
  }

  /**
   * Logout and clear all session data
   */
  logout() {
    try {
      // Clear all session data
      SessionManager.clearSession();
      this.currentUser = null;
      
      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, try to clear what we can
      SessionManager.clearSession();
      this.currentUser = null;
      
      return {
        success: false,
        message: 'Logout completed with errors'
      };
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated() {
    return SessionManager.isAuthenticated();
  }

  /**
   * Get current authentication token
   */
  getToken() {
    return SessionManager.getToken();
  }

  /**
   * Get current user information
   */
  getCurrentUser() {
    if (this.currentUser) {
      return this.currentUser;
    }
    
    // Try to load from storage
    this.currentUser = SessionManager.getUser();
    return this.currentUser;
  }

  /**
   * Validate current session with server
   * This would typically make a request to validate the token
   */
  async validateSession() {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      // For now, we'll just check if token exists and is not expired
      // In a real implementation, you might want to make a request to /auth/validate
      const token = this.getToken();
      if (!token) {
        this.logout();
        return false;
      }

      // Check if token is expiring soon
      if (SessionManager.isTokenExpiringSoon(5)) {
        console.warn('Token expiring soon');
        // In a real implementation, you might want to refresh the token here
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Refresh authentication token
   * Note: This would need to be implemented based on your API's refresh token mechanism
   */
  async refreshToken() {
    try {
      // This is a placeholder - implement based on your API's refresh mechanism
      // For now, we'll just validate the existing token
      const isValid = await this.validateSession();
      
      if (!isValid) {
        throw new Error('Session expired');
      }

      return {
        success: true,
        message: 'Token is still valid'
      };
    } catch (error) {
      this.logout();
      throw new Error('Failed to refresh token: ' + error.message);
    }
  }

  /**
   * Handle authentication errors (e.g., from API responses)
   */
  handleAuthError(error) {
    const errorInfo = ErrorHandler.handleApiError(error, 'Authentication');
    
    if (errorInfo.shouldRedirect) {
      this.logout();
      // Redirect to login page
      window.location.href = '/admin';
    }
    
    return errorInfo;
  }

  /**
   * Check if login is currently in progress
   */
  isLoginInProgress() {
    return this.isLoggingIn;
  }

  /**
   * Get authentication headers for API requests
   */
  getAuthHeaders() {
    const token = this.getToken();
    if (!token) {
      return {};
    }

    return {
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Initialize authentication state on app startup
   */
  async initialize() {
    try {
      // Load user from storage
      this.currentUser = SessionManager.getUser();
      
      // Validate session if user exists
      if (this.currentUser) {
        const isValid = await this.validateSession();
        if (!isValid) {
          this.currentUser = null;
        }
      }
      
      return this.isAuthenticated();
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.logout();
      return false;
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;